import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { adviceRooms, guests } from 'api';
import { isStreamingEnabled } from 'helpers/features';
import { showApiErrorToast } from 'helpers/toasts';
import {
  concatUint8Arrays,
  getSilentFrame,
  mediaStreamCodec,
  SAMPLE_RATE,
} from 'helpers/transcriptionAudioStream';
import { fetchAdviceRoom, fetchGuestAdviceRoom } from 'store/adviceRooms/actions';
import { useGetAdviceRoomSummaryState } from 'store/adviceRooms/selectors';
import { RoomConnectionState } from 'components/VideoChat/RoomConnectionState';

interface Props {
  adviceRoomId?: number;
  connectionState: RoomConnectionState;
  isAudioEnabled: boolean;
  authToken?: string;
  selectedAudioDeviceId?: string;
  setTranscriptionEnabled: (enabled: boolean) => void;
}
export function useManageLiveTranscription({
  adviceRoomId,
  isAudioEnabled,
  selectedAudioDeviceId,
  authToken,
  connectionState,
  setTranscriptionEnabled,
}: Props) {
  const dispatch = useDispatch();
  const isAudioPaused = useRef(true);
  const socket = useRef<WebSocket | null>(null);
  const mediaStream = useRef<MediaStream | null>(null);
  const worklet = useRef<AudioWorkletNode | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const audioBuffer = useRef<Uint8Array[]>([]);
  const [closureCount, setClosureCount] = useState(0);

  // Buffer size limit to prevent memory issues (approximately 1 second of audio at 44.1kHz)
  const MAX_BUFFER_SIZE = 50;

  const enableMeetingSummary = useGetAdviceRoomSummaryState(adviceRoomId);

  const shouldResumeTranscription = useMemo(() => {
    return (
      connectionState === RoomConnectionState.Connected && isAudioEnabled && enableMeetingSummary
    );
  }, [connectionState, isAudioEnabled, enableMeetingSummary]);

  const sendTranscriptionToServer = useCallback(
    ({ end, text }: { end: number; text: string }) => {
      if (!adviceRoomId) return;
      const timestamp = new Date();
      const startTime = timestamp.toISOString();

      timestamp.setSeconds(timestamp.getSeconds() + end);
      const endTime = timestamp.toISOString();

      const dataPayload = {
        segment: {
          text,
          startTime,
          endTime,
        },
      };
      if (authToken) {
        guests()
          .clientMeetingRooms()
          .sendTranscription({
            ...dataPayload,
            authToken,
          });
      } else {
        adviceRooms(adviceRoomId)
          .transcriptions()
          .POST({
            ...dataPayload,
          });
      }
    },
    [adviceRoomId, authToken]
  );

  const startTranscription = useCallback(async () => {
    if (!isStreamingEnabled()) return;
    if (!adviceRoomId) return;

    // If socket is already running don't create a new socket connection.
    // Ready State: Connecting: 0, Open: 1, Closing: 2, Closed: 2
    if (socket.current && socket.current.readyState <= socket.current.OPEN) return;

    let wsURL = '';
    if (authToken) {
      const {
        data: { data },
      } = await guests().clientMeetingRooms().getWebsocketURL(authToken);

      wsURL = data.attributes.url;
    } else {
      const {
        data: { data },
      } = await adviceRooms(adviceRoomId).transcriptions().getWebsocketURL();

      wsURL = data.attributes.url;
    }

    socket.current = new WebSocket(wsURL);
    socket.current.binaryType = 'arraybuffer';

    socket.current.onmessage = message => {
      const decoded = mediaStreamCodec.decode(new Uint8Array(message.data));
      const jsonText = new TextDecoder('utf-8').decode(decoded.body);
      const response = JSON.parse(jsonText);

      const results = response.Transcript?.Results;
      if (results && results.length && !results[0]?.IsPartial) {
        const result = results[0].Alternatives[0];

        let text = '';
        if (result.Items && result.Items.length > 0) {
          result.Items.forEach((item: { Type: string; Content: string; Confidence: number }) => {
            if (item.Type === 'punctuation') text += `${item.Content}`;
            if (item.Confidence > 0.2) text += ` ${item.Content}`;
          });
        }

        if (text.length > 0) {
          const end = result.Items[result.Items.length - 1].EndTime;
          sendTranscriptionToServer({ end, text });
        }
      }
    };

    socket.current.onclose = () => {
      setClosureCount(prev => prev + 1);
    };

    socket.current.onerror = () => {
      showApiErrorToast(new Error('Error connecting transciption servers.'));
    };
  }, [adviceRoomId, sendTranscriptionToServer, authToken]);

  const sendAudioBufferToAWSTranscribe = useCallback(async () => {
    if (!socket.current || socket.current.readyState !== socket.current.OPEN) return;

    if (audioBuffer.current.length === 0) return;

    const maxChunk = 4800;
    const buffer = concatUint8Arrays(audioBuffer.current);

    audioBuffer.current = [];

    try {
      const chunks: Uint8Array[] = [];

      for (let i = 0; i < buffer.length; i += maxChunk) {
        const chunk = buffer.subarray(i, Math.min(i + maxChunk, buffer.length));
        chunks.push(chunk);
      }

      chunks.forEach(chunk => {
        if (!socket.current || socket.current.readyState !== socket.current.OPEN) {
          return;
        }

        const message = mediaStreamCodec.encode({
          headers: {
            ':message-type': { type: 'string', value: 'event' },
            ':event-type': { type: 'string', value: 'AudioEvent' },
          },
          body: chunk,
        });

        try {
          socket.current.send(message);
        } catch (error) {
          showApiErrorToast(error);
        }
      });
    } catch (error) {
      showApiErrorToast(error);
      audioBuffer.current = [];
    }
  }, []);

  const resumeTranscription = useCallback(async () => {
    if (audioContext.current && audioContext.current.state === 'suspended') {
      return audioContext.current.resume();
    }

    isAudioPaused.current = false;
  }, []);

  const pauseTranscription = useCallback(async () => {
    isAudioPaused.current = true;
  }, []);

  const stopTranscription = useCallback(() => {
    audioContext.current?.close();
    socket?.current?.close();
    worklet.current?.port.close();
    worklet.current?.disconnect();

    const tracks = mediaStream.current?.getTracks();
    tracks?.forEach(track => {
      track.stop();
    });

    audioContext.current = null;
    socket.current = null;
    worklet.current = null;
    mediaStream.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopTranscription();
    };
  }, [stopTranscription]);

  useEffect(() => {
    if (!adviceRoomId) return;

    if (authToken) {
      dispatch(fetchGuestAdviceRoom(authToken));
    } else {
      dispatch(fetchAdviceRoom(adviceRoomId));
    }
  }, [adviceRoomId, dispatch, authToken]);

  useEffect(() => {
    setTranscriptionEnabled(enableMeetingSummary);
  }, [enableMeetingSummary, setTranscriptionEnabled]);

  useEffect(() => {
    if (!isStreamingEnabled()) return;
    if (
      enableMeetingSummary &&
      connectionState === RoomConnectionState.Connected &&
      (!socket.current || socket.current.readyState > socket.current.OPEN)
    ) {
      startTranscription();
    }
  }, [enableMeetingSummary, startTranscription, connectionState, closureCount]);

  useEffect(() => {
    if (!isStreamingEnabled()) return;

    sendAudioBufferToAWSTranscribe();
    if (shouldResumeTranscription) {
      resumeTranscription();
    } else {
      pauseTranscription();
    }
  }, [
    pauseTranscription,
    resumeTranscription,
    sendAudioBufferToAWSTranscribe,
    shouldResumeTranscription,
  ]);

  useEffect(() => {
    if (connectionState === RoomConnectionState.Disconnected) return;
    (async () => {
      if (!audioContext.current) {
        audioContext.current = new AudioContext();
        await audioContext.current.audioWorklet.addModule('./pcm-processor.js');
      }

      if (!mediaStream.current) {
        mediaStream.current = await navigator.mediaDevices.getUserMedia({
          audio: { sampleRate: SAMPLE_RATE, channelCount: 1 },
        });
      }

      if (!worklet.current) {
        const source = audioContext.current.createMediaStreamSource(mediaStream.current);
        worklet.current = new AudioWorkletNode(audioContext.current, 'pcm-processor');
        source.connect(worklet.current).connect(audioContext.current.destination);

        worklet.current.port.onmessage = event => {
          if (isAudioPaused.current) {
            audioBuffer.current.push(getSilentFrame());
          } else {
            audioBuffer.current.push(new Uint8Array(event.data));
          }

          // Prevent buffer overflow by limiting size
          if (audioBuffer.current.length > MAX_BUFFER_SIZE) {
            audioBuffer.current = audioBuffer.current.slice(-MAX_BUFFER_SIZE);
          }

          if (audioBuffer.current.length >= 4) {
            sendAudioBufferToAWSTranscribe();
          }
        };

        worklet.current.onprocessorerror = () => {
          showApiErrorToast(new Error('Error handling processor worklet.'));
        };

        source.connect(worklet.current).connect(audioContext.current.destination);
        worklet.current.port.start();
      }
    })();
  }, [
    selectedAudioDeviceId,
    connectionState,
    sendAudioBufferToAWSTranscribe,
    shouldResumeTranscription,
  ]);
}
